import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, Chip, CircularProgress, Paper, Stack, Tab, Tabs, Typography } from '@mui/material';
import { ArrowBack, Place } from '@mui/icons-material';
import AppNavigation from './AppNavigation';
import SignInNudge from './SignInNudge';
import PostCard from './PostCard';
import DiverRow from './DiverRow';
import InviteButton from './InviteButton';
import { useNotification } from './NotificationContext';
import { authService } from '../services/authService';
import { followService } from '../services/followService';
import { UserPublicProfile } from '../services/buddyService';
import { userProfileService, CountryMembers, RegionCounts } from '../services/userProfileService';
import { postService, Post } from '../services/postService';
import { useDiveSiteCatalog } from '../hooks/useDiveSiteCatalog';
import { buildCountries, buildRegions, regionLabel } from '../utils/regions';

/**
 * A country's page: its dive regions (each a community of its own), a feed of public posts from divers who call the
 * country home, and those divers. Everything about who lives where comes from the server's region index.
 */
const CountryCommunity: React.FC = () => {
  const { slug = '' } = useParams();
  const navigate = useNavigate();
  const { showError } = useNotification();
  const { allSites, loading: sitesLoading } = useDiveSiteCatalog();
  const [currentUser, setCurrentUser] = useState(authService.getUser());
  const userId = currentUser?.userId ?? null;
  const [tab, setTab] = useState(0);

  const [members, setMembers] = useState<CountryMembers | null>(null);
  const [regionCounts, setRegionCounts] = useState<RegionCounts>({});
  const [posts, setPosts] = useState<Post[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(true);
  const [loadingPosts, setLoadingPosts] = useState(true);
  const [followedIds, setFollowedIds] = useState<Set<string>>(new Set());

  const country = useMemo(() => buildCountries(buildRegions(allSites)).find(c => c.slug === slug) ?? null, [allSites, slug]);
  const countryKnown = !!country;

  useEffect(() => {
    if (!userId) { setLoadingMembers(false); setLoadingPosts(false); return; }
    if (!countryKnown) return;
    let cancelled = false;
    setLoadingMembers(true);
    setLoadingPosts(true);
    userProfileService.getCountryMembers(slug)
      .then(m => { if (!cancelled) setMembers(m); })
      .catch(() => { if (!cancelled) setMembers(null); })
      .finally(() => { if (!cancelled) setLoadingMembers(false); });
    userProfileService.getRegionCounts().then(c => { if (!cancelled) setRegionCounts(c); }).catch(() => {});
    postService.getCommunityFeed({ countrySlug: slug })
      .then(p => { if (!cancelled) setPosts(p); })
      .catch(() => { if (!cancelled) setPosts([]); })
      .finally(() => { if (!cancelled) setLoadingPosts(false); });
    return () => { cancelled = true; };
  }, [userId, slug, countryKnown]);

  const handleFollow = useCallback(async (diver: UserPublicProfile) => {
    try {
      await followService.follow(diver.userId);
      setFollowedIds(prev => new Set(prev).add(diver.userId));
    } catch {
      showError(`Could not follow ${diver.displayName}`);
    }
  }, [showError]);

  if (!userId) {
    return (
      <Box>
        <AppNavigation />
        <SignInNudge feature="explore this country's dive community" onSignedIn={() => setCurrentUser(authService.getUser())} />
      </Box>
    );
  }

  if (sitesLoading && !country) {
    return <Box><AppNavigation /><Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box></Box>;
  }

  if (!country) {
    return (
      <Box>
        <AppNavigation />
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography variant="h6">We couldn't find that country</Typography>
          <Button sx={{ mt: 2 }} onClick={() => navigate('/communities')}>Browse all communities</Button>
        </Box>
      </Box>
    );
  }

  const regionsByActivity = [...country.regions].sort((a, b) => {
    const count = (slugOf: string) => (regionCounts[slugOf]?.locals ?? 0) + (regionCounts[slugOf]?.joined ?? 0);
    return count(b.slug) - count(a.slug) || b.sites.length - a.sites.length;
  });
  const shown = members?.members ?? [];

  return (
    <Box>
      <AppNavigation />
      <Box sx={{ px: { xs: 2, sm: 3 }, pb: 4, maxWidth: 760, mx: 'auto' }}>
        <Button size="small" startIcon={<ArrowBack />} onClick={() => navigate('/communities')} sx={{ mt: 2 }}>All communities</Button>

        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 1, mb: 0.5 }}>
          <Typography variant="h4" sx={{ fontWeight: 800 }}>{country.name}</Typography>
          <InviteButton />
        </Stack>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          <Chip size="small" variant="outlined" label={`${country.regions.length} region${country.regions.length === 1 ? '' : 's'}`} />
          <Chip size="small" variant="outlined" label={`${country.siteCount} dive site${country.siteCount === 1 ? '' : 's'}`} />
          {members && <Chip size="small" color="primary" variant="outlined" label={`${members.memberCount} diver${members.memberCount === 1 ? '' : 's'} based here`} />}
        </Stack>

        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto" sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tab label="Regions" />
          <Tab label="Feed" />
          <Tab label="Divers" />
        </Tabs>

        {tab === 0 && (
          <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
            {regionsByActivity.map(r => {
              const c = regionCounts[r.slug];
              const total = (c?.locals ?? 0) + (c?.joined ?? 0);
              return (
                <Paper
                  key={r.slug}
                  variant="outlined"
                  onClick={() => navigate(`/communities/${r.slug}`)}
                  sx={{ p: 2, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                    <Place fontSize="small" color="primary" />
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{regionLabel({ region: r.region, country: '' })}</Typography>
                  </Stack>
                  <Stack direction="row" spacing={1}>
                    <Chip size="small" variant="outlined" label={`${r.sites.length} site${r.sites.length === 1 ? '' : 's'}`} />
                    {total > 0 && <Chip size="small" color="primary" variant="outlined" label={`${total} member${total === 1 ? '' : 's'}`} />}
                  </Stack>
                </Paper>
              );
            })}
          </Box>
        )}

        {tab === 1 && (
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              Public posts from the last 30 days by divers based in {country.name}.
            </Typography>
            {loadingPosts && <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>}
            {!loadingPosts && posts.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 5 }}>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>No public posts here yet</Typography>
                <Typography variant="body2" color="text.secondary">Set a home site in {country.name} and post publicly to start the conversation.</Typography>
              </Box>
            )}
            {posts.map(post => (
              <PostCard key={post.postId} post={post} currentUserId={userId} onDelete={() => setPosts(prev => prev.filter(p => p.postId !== post.postId))} />
            ))}
          </Box>
        )}

        {tab === 2 && (
          <Stack spacing={1}>
            {loadingMembers && <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>}
            {!loadingMembers && shown.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                No divers list a home site in {country.name} yet — be the first, or invite a buddy.
              </Typography>
            )}
            {[...shown].sort((a, b) => (b.diveCount ?? 0) - (a.diveCount ?? 0)).map(diver => (
              <DiverRow
                key={diver.userId}
                diver={diver}
                isSelf={diver.userId === userId}
                followed={followedIds.has(diver.userId) || diver.isFollowing}
                onFollow={handleFollow}
              />
            ))}
            {members && members.memberCount - shown.length > 0 && (
              <Typography variant="caption" color="text.secondary">Showing {shown.length} of {members.memberCount} divers.</Typography>
            )}
          </Stack>
        )}
      </Box>
    </Box>
  );
};

export default CountryCommunity;
